import React from 'react';
import PropTypes from 'prop-types';
import {
  Modal,
  ModalHeader,
  ModalContent,
  ModalActions,
  Button,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHeaderCell,
  TableCell,
  Grid,
  GridRow,
  GridColumn,
  Message,
  MessageHeader,
} from 'semantic-ui-react';
import autoBind from 'auto-bind';
import { format, parseISO } from 'date-fns';
import LoadingSpinner from 'components/LoadingSpinner/LoadingSpinner';
import { formatDateTime } from 'utils/dateTimeUtils';
import { formatCurrencyRupiah } from 'utils/currencyUtils';
import {
  renderCouponStatusText,
  renderSubscriptionLength,
} from 'components/renderUtils';
import DefaultPagination from 'components/DefaultPagination';

class CouponDetailsModal extends React.Component {
  constructor(props) {
    super(props);
    autoBind(this);
  }

  onLogPageChange(event, { activePage }) {
    const { onCouponLogsPageChange } = this.props;
    onCouponLogsPageChange(activePage);
  }

  renderCouponDetails() {
    const { coupon } = this.props;
    const {
      code,
      created_at: createdAt,
      ended_at: endedAt,
      amount,
      max_discount_per_application: maxDiscountPerApplication,
      additional_month: additionalMonth,
      includes_all_businesses: includesAllBusinesses,
      used_quota: usedQuota,
      global_quota: globalQuota,
    } = coupon;

    return (
      <Grid columns={3}>
        <GridRow>
          <GridColumn>
            Coupon code
            <br />
            <strong>{code}</strong>
          </GridColumn>
          <GridColumn>
            Date created
            <br />
            <strong>{format(parseISO(createdAt), 'd MMMM yyyy')}</strong>
          </GridColumn>
          <GridColumn>
            Expiration Date
            <br />
            <strong>{format(parseISO(endedAt), 'd MMMM yyyy')}</strong>
          </GridColumn>
        </GridRow>

        <GridRow>
          <GridColumn>
            Percentage
            <br />
            <strong>{amount || 0}%</strong>
          </GridColumn>
          <GridColumn>
            Max Discount
            <br />
            <strong>
              {maxDiscountPerApplication
                ? formatCurrencyRupiah(maxDiscountPerApplication)
                : '-'}
            </strong>
          </GridColumn>
          <GridColumn>
            Additional Months
            <br />
            <strong>{additionalMonth || 0} Months</strong>
          </GridColumn>
        </GridRow>

        <GridRow>
          <GridColumn>
            Quota Type
            <br />
            <strong>
              {includesAllBusinesses ? 'Coupon Quantity' : 'Merchant Quantity'}
            </strong>
          </GridColumn>
          <GridColumn>
            Coupon used
            <br />
            <strong>
              {usedQuota}/{globalQuota}
            </strong>
          </GridColumn>
          <GridColumn>
            Subcription Length
            <br />
            <strong>{renderSubscriptionLength(coupon)}</strong>
          </GridColumn>
        </GridRow>

        <GridRow>
          <GridColumn>
            Status
            <br />
            <strong>{renderCouponStatusText(coupon)}</strong>
          </GridColumn>
        </GridRow>
      </Grid>
    );
  }

  renderCouponLogs() {
    const { logs } = this.props;

    return (
      <>
        <Table celled>
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Time</TableHeaderCell>
              <TableHeaderCell>Logs</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {logs.list.map(log => (
              <TableRow>
                <TableCell>{formatDateTime(parseISO(log.time))}</TableCell>
                <TableCell>{log.log}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <DefaultPagination
          totalCount={logs.totalCount}
          activePage={logs.pageNumber}
          pageSize={logs.pageSize}
          onPageChange={this.onLogPageChange}
        />
      </>
    );
  }

  render() {
    const {
      shown,
      onClose,
      isLoading,
      coupon,
      errorShown,
      errorMessage,
      isLogsLoading,
    } = this.props;
    return (
      <Modal open={shown} onClose={onClose}>
        <ModalHeader>Coupon Details</ModalHeader>
        <ModalContent>
          <Message negative hidden={!errorShown}>
            <MessageHeader>Error occured</MessageHeader>
            <ul>
              {errorMessage && errorMessage.map ? (
                errorMessage.map(error => <li>{error}</li>)
              ) : (
                <li>{errorMessage}</li>
              )}
            </ul>
          </Message>
          {isLoading ? (
            <LoadingSpinner />
          ) : (
            <div>
              {coupon !== null && this.renderCouponDetails()}
              {isLogsLoading ? <LoadingSpinner /> : this.renderCouponLogs()}
            </div>
          )}
        </ModalContent>
        <ModalActions>
          <Button onClick={onClose}>Close</Button>
        </ModalActions>
      </Modal>
    );
  }
}

CouponDetailsModal.propTypes = {
  shown: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  coupon: PropTypes.shape({
    code: PropTypes.string,
    status: PropTypes.string,
    started_at: PropTypes.instanceOf(Date),
    ended_at: PropTypes.instanceOf(Date),
    amount: PropTypes.number,
    benefit_type: PropTypes.string,
    max_discount_per_application: PropTypes.number,
    additional_month: PropTypes.number,
    used_quota: PropTypes.number,
    global_quota: PropTypes.number,
    created_at: PropTypes.instanceOf(Date),
    includes_all_businesses: PropTypes.bool,
  }),
  isLoading: PropTypes.bool.isRequired,
  errorShown: PropTypes.bool.isRequired,
  errorMessage: PropTypes.string,
  logs: PropTypes.shape({
    list: PropTypes.array,
    pageNumber: PropTypes.number.isRequired,
    pageSize: PropTypes.number.isRequired,
    totalCount: PropTypes.number.isRequired,
  }).isRequired,
  isLogsLoading: PropTypes.bool.isRequired,
  onCouponLogsPageChange: PropTypes.func.isRequired,
};

CouponDetailsModal.defaultProps = {
  coupon: null,
  errorMessage: null,
};

export default CouponDetailsModal;
